/* eslint-disable @typescript-eslint/naming-convention */

// keep up to date with services/bookstore/catalog/choices.py:Suppliers
import type { AnyDocument, FirstClassDocument, PartialDocument } from './index';

export enum BookstoreSupplierSlug {
  Unknown = 'unknown',

  IngramCdf = 'ingram-cdf',
  HarpercollinsReadwise = 'harpercollins-readwise',
  IngramCoreSourcePlus = 'ingram',
  Abrams = 'abrams',
  AfterDinner = 'after-dinner',
  Albinmichel = 'albinmichel',
  Andrewsmcmeel = 'andrewsmcmeel',
  Baker = 'baker',
  BakerTaylor = 'baker-taylor',
  Bayard = 'bayard',
  BloomsburyUk = 'bloomsbury-uk',
  BloomsburyUs = 'bloomsbury-us',
  Bnf = 'bnf',
  Cde = 'cde',
  Cned = 'cned',
  Editis = 'editis',
  Edyllio = 'edyllio',
  Flammarion = 'flammarion',
  Gallimard = 'gallimard',
  Garp = 'garp',
  HachetteAudioUs = 'hachette-audio-us',
  HachetteFr = 'hachette-fr',
  HachetteUk = 'hachette-uk',
  HarlequinUs = 'harlequin-us',
  Harpercollins = 'harpercollins',
  HarpercollinsUk = 'harpercollins-uk',
  Hbgusa = 'hbgusa',
  Lerner = 'lerner',
  MacmillanAudioUs = 'macmillan-audio-us',
  MacmillanUk = 'macmillan-uk',
  Macmillan = 'macmillan',
  Mcgrawhill = 'mcgrawhill',
  OpenRoadMedia = 'open-road-media',
  Packt = 'packt',
  PrhAudioUk = 'prh-audio-uk',
  PrhAudioUs = 'prh-audio-us',
  PrhUkRandomhouse = 'randomhouse-uk',
  PrhUkPenguin = 'penguin-uk',
  Randomhouse = 'randomhouse',
  SageUk = 'sage-uk',
  SimonSchuster = 'simon-schuster',
  SimonSchusterAudioUs = 'simon-schuster-audio-us',
  SimonSchusterUk = 'simon-schuster-uk',
  Sofedis = 'sofedis',
  Tyndale = 'tyndale',
  Volumen = 'volumen',
  Wiley = 'wiley',
  ReadwiseTest = 'readwise-test',
  LionHudson = 'lionhudson',
  Kogan = 'kogan-uk',
  Onisep = 'onisep',
  AmazonStore = 'amazon-store',
  Hardcover = 'hardcover',
  Goodreads = 'goodreads',
}

// Pulled from https://bookwise.com/admin/catalog/supplier/, update if we add another Supplier.
// IDs kept internal to this file because supplier slug is easier to work with and more debuggable.
export const SupplierIdToSlug: { [supplierId: number]: BookstoreSupplierSlug; } = {
  154: BookstoreSupplierSlug.Goodreads,
  153: BookstoreSupplierSlug.Hardcover,
  152: BookstoreSupplierSlug.AmazonStore,
  51: BookstoreSupplierSlug.Onisep,
  50: BookstoreSupplierSlug.LionHudson,
  49: BookstoreSupplierSlug.Kogan,
  48: BookstoreSupplierSlug.Wiley,
  47: BookstoreSupplierSlug.Volumen,
  46: BookstoreSupplierSlug.Tyndale,
  45: BookstoreSupplierSlug.Sofedis,
  44: BookstoreSupplierSlug.SimonSchusterUk,
  43: BookstoreSupplierSlug.SimonSchusterAudioUs,
  42: BookstoreSupplierSlug.SimonSchuster,
  41: BookstoreSupplierSlug.SageUk,
  40: BookstoreSupplierSlug.Randomhouse,
  39: BookstoreSupplierSlug.PrhUkPenguin,
  38: BookstoreSupplierSlug.PrhUkRandomhouse,
  37: BookstoreSupplierSlug.PrhAudioUs,
  36: BookstoreSupplierSlug.PrhAudioUk,
  35: BookstoreSupplierSlug.Packt,
  34: BookstoreSupplierSlug.OpenRoadMedia,
  33: BookstoreSupplierSlug.Mcgrawhill,
  32: BookstoreSupplierSlug.Macmillan,
  31: BookstoreSupplierSlug.MacmillanUk,
  30: BookstoreSupplierSlug.MacmillanAudioUs,
  29: BookstoreSupplierSlug.Lerner,
  28: BookstoreSupplierSlug.Hbgusa,
  27: BookstoreSupplierSlug.HarpercollinsUk,
  26: BookstoreSupplierSlug.Harpercollins,
  25: BookstoreSupplierSlug.HarlequinUs,
  24: BookstoreSupplierSlug.HachetteUk,
  23: BookstoreSupplierSlug.HachetteFr,
  22: BookstoreSupplierSlug.HachetteAudioUs,
  21: BookstoreSupplierSlug.Garp,
  20: BookstoreSupplierSlug.Gallimard,
  19: BookstoreSupplierSlug.Flammarion,
  18: BookstoreSupplierSlug.Edyllio,
  17: BookstoreSupplierSlug.Editis,
  16: BookstoreSupplierSlug.Cned,
  15: BookstoreSupplierSlug.Cde,
  14: BookstoreSupplierSlug.Bnf,
  13: BookstoreSupplierSlug.BloomsburyUs,
  12: BookstoreSupplierSlug.BloomsburyUk,
  11: BookstoreSupplierSlug.Bayard,
  10: BookstoreSupplierSlug.BakerTaylor,
  9: BookstoreSupplierSlug.Baker,
  8: BookstoreSupplierSlug.Andrewsmcmeel,
  7: BookstoreSupplierSlug.Albinmichel,
  6: BookstoreSupplierSlug.AfterDinner,
  5: BookstoreSupplierSlug.Abrams,
  4: BookstoreSupplierSlug.IngramCoreSourcePlus,
  3: BookstoreSupplierSlug.IngramCdf,
  2: BookstoreSupplierSlug.ReadwiseTest,
};


// Bit allocations
const FLAG_BITS = 1;
const ISBN_BITS = 44;
const SUPPLIER_BITS = 15;

// Masks
// Use BigInt() function calls instead of `1n` literals for es2019/safari13.1 build target compatibility
const FLAG_MASK = (BigInt(1) << BigInt(FLAG_BITS)) - BigInt(1);
const ISBN_MASK = (BigInt(1) << BigInt(ISBN_BITS)) - BigInt(1);
const SUPPLIER_MASK = (BigInt(1) << BigInt(SUPPLIER_BITS)) - BigInt(1);

// Shifts
const SUPPLIER_SHIFT = BigInt(ISBN_BITS);
const FLAG_SHIFT = BigInt(ISBN_BITS + SUPPLIER_BITS);

// adapted from decode_product_id() in Bookstore Python
function decodeProductId(packed: string): { supplierId: number; isbn: number; } | null {
  const packedBigInt = BigInt(packed);

  const flag = Number(packedBigInt >> FLAG_SHIFT & FLAG_MASK);

  if (flag !== 1) {
    return null;
  }

  const supplierId = Number(packedBigInt >> SUPPLIER_SHIFT & SUPPLIER_MASK);
  const isbn = Number(packedBigInt & ISBN_MASK);

  return { supplierId, isbn };
}

export function _extractSupplierSlugFromProductId(catalogProductId: string): BookstoreSupplierSlug | null {
  // exported only for testing
  const unpacked = decodeProductId(catalogProductId);
  if (!unpacked) {
    return null;
  }
  return SupplierIdToSlug[unpacked.supplierId] ?? null;
}

function isSupplierPenguinRandomHouse(supplierSlug: BookstoreSupplierSlug): boolean {
  return [
    BookstoreSupplierSlug.PrhAudioUk,
    BookstoreSupplierSlug.PrhAudioUs,
    BookstoreSupplierSlug.PrhUkRandomhouse,
    BookstoreSupplierSlug.PrhUkPenguin,
    BookstoreSupplierSlug.Randomhouse,
  ].includes(supplierSlug);
}

function isSupplierHarperCollins(supplierSlug: BookstoreSupplierSlug): boolean {
  return [
    BookstoreSupplierSlug.Harpercollins,
    BookstoreSupplierSlug.HarpercollinsReadwise,
    BookstoreSupplierSlug.HarpercollinsUk,
    BookstoreSupplierSlug.Hbgusa,
  ].includes(supplierSlug);
}

export function _highlightCopyWordCountLimitForSupplier(supplierSlug: BookstoreSupplierSlug): number | null {
  // exported only for testing
  if (isSupplierPenguinRandomHouse(supplierSlug)) {
    return 300;
  }
  if (isSupplierHarperCollins(supplierSlug)) {
    // TODO(mitch): for the PRH audit, for their testing we're enabling highlight copy limitations
    //  on HarperCollins because we're not yet allowed to sell PRH books.
    return 300;
  }
  return null;
}

function extractSupplierSlugFromDocument(
  doc: PartialDocument<AnyDocument, 'source_specific_data'>,
): BookstoreSupplierSlug | null {
  const catalogProductId = doc?.source_specific_data?.bookstore?.catalog_product_id;
  if (!catalogProductId) {
    // TODO: kinda insecure, users can modify catalog_product_id client-side and bypass restrictions.
    return null;
  }

  return _extractSupplierSlugFromProductId(catalogProductId);
}

// TODO: Migrate to use BookstoreProductFeatures instead of hardcoded supplier slug lists.
export function _shouldApplyDRMLimitsToSupplier(_supplierSlug: BookstoreSupplierSlug): boolean {
  // The server issues + revokes a per-device license for every DRM-protected book regardless of
  // supplier, and every publisher we carry (PRH, HarperCollins, Hachette, Macmillan, ...) imposes the
  // same per-device limit — so the device-limit checks apply to all bookstore suppliers, not just
  // PRH/HC. Kept per-supplier-shaped so an individual supplier can be carved back out if needed.
  return true;
}

export function shouldApplyDRMLimitsToDocument(
  doc: PartialDocument<AnyDocument, 'source_specific_data'> | null | undefined,
): boolean {
  if (!doc) {
    return false;
  }
  const supplierSlug = extractSupplierSlugFromDocument(doc);
  if (!supplierSlug) {
    return false;
  }
  return _shouldApplyDRMLimitsToSupplier(supplierSlug);
}

export function getCopyWordLimitForDocument(
  doc: PartialDocument<FirstClassDocument, 'source_specific_data'> | null | undefined,
): number | null {
  if (!doc) {
    return null;
  }
  const supplierSlug = extractSupplierSlugFromDocument(doc);
  if (!supplierSlug) {
    return null;
  }
  const limit = _highlightCopyWordCountLimitForSupplier(supplierSlug);
  if (!limit) {
    return null;
  }
  return Math.max(0, limit - 50);
}

export function shouldDisableImageModalShareActions(
  doc: PartialDocument<AnyDocument, 'source_specific_data'> | null | undefined,
): boolean {
  if (!doc) {
    return false;
  }
  const supplierSlug = extractSupplierSlugFromDocument(doc);
  if (!supplierSlug) {
    return false;
  }
  return isSupplierHarperCollins(supplierSlug);
}

// Opus API types

export type BookstoreProductFormat =
  | 'cd_audio'
  | 'audiobook'
  | 'ebook'
  | 'hardback'
  | 'leather'
  | 'loose_leaf'
  | 'other'
  | 'paperback'
  | 'spiral_bound';

export type OpusAuthor = {
  name: string;
  url: string;
  isni: string | null;
};

export type OpusDiscount = {
  discount_code?: string;
  discount_code_type?: string;
  discount_code_type_name?: string;
  discount_level?: string;
};

export type OpusTerritory = {
  CountriesIncluded?: string;
};

export type OpusPrice = {
  price_amount: string;
  currency_code: string;
  qualifier: string | null;
  supplier: string;
  price_type: string | null;
  price_status: string | null;
  unpriced_item_type: string | null;
  effective_from: string | null;
  effective_until: string | null;
  country_code: string[];
  discounts: OpusDiscount[];
  territory: OpusTerritory[];
  original_amount: string;
  max_amount: string;
};

export type OpusEntitlement = {
  owns_sample: boolean;
  owns_full_product: boolean;
};

export type OpusCartData = {
  id?: string;
  opus_id?: string;
  title?: string;
  author?: string;
  format?: BookstoreProductFormat;
  url?: string;
  cover_url?: string;
  price?: string;
  price_cents?: string;
  currency?: string;
  supplier?: string;
};

export type OpusProduct = {
  id: string;
  isbn: string | null;
  ean: string;
  asin: string | null;
  title: string;
  subtitle: string | null;
  full_title: string;
  authors: OpusAuthor[];
  authors_display: string;
  publishing_date: string | null;
  language_code: string | null;
  page_count: string | null;
  word_count: string | null;
  reading_time: string | null;
  publisher: string | null;
  imprint: string | null;
  summary: string | null;
  format: BookstoreProductFormat;
  cover_url: string | null;
  thumbnail_url: string | null;
  cover_alt: string;
  is_physical: boolean;
  available_since: string | null;
  available_until: string | null;
  sample_allowed: boolean;
  categories: string[];
  desire: string;
  entitlement: OpusEntitlement;
  available: boolean;
  available_in_time_in_words: string | null;
  price: OpusPrice | null;
  cart_data: OpusCartData;
  structured_data: string;
};

export type OpusVariant = {
  id?: string;
  ean: string;
  format: BookstoreProductFormat;
  price: OpusPrice | null;
  available: boolean;
  available_in_time_in_words: string | null;
};

export type Opus = {
  opus_id: string;
  available_formats: BookstoreProductFormat[];
  products: OpusProduct[];
  variants: OpusVariant[];
  show_all_formats: boolean;
  available: boolean;
  available_in_time_in_words: string | null;
  title: string;
  full_title: string;
  authors: string;
  published_year: string;
  cover_url: string | null;
  thumbnail_url: string | null;
  price: OpusPrice | null;
  url: string;
};

export type OpusErrorResponse = {
  error: string;
};

// Authors API types

export type AuthorWithBooks = {
  contributor_id: string;
  name: string;
  url: string;
  isni: string | null;
  biogram: string;
  biogram_text: string | null;
  books: Opus[];
};

export type AuthorsResponse = {
  product_id: string;
  authors: AuthorWithBooks[];
};
